/**
 * Resolves the current platform access mode from the auth store.
 * AuthContext is loaded during authentication and persisted into AuthStore,
 * so route guards can make consistent decisions without route-time API calls.
 * Unknown or missing modes are not classified as tenant access.
 */
import { Injectable } from '@angular/core';
import { PLATFORM_MODES, PlatformMode } from '@app/core/models/platform-mode.model';
import { LoggingService } from '@app/core/services';
import { AuthStore } from '@app/core/state/auth.store';
import {
    APP_LAYOUTS,
    APP_ROUTES,
    AppLayoutType,
} from '@modules/navigation/models';

type ResolvedAccessMode = 'tenant-base' | 'api-tenant' | 'admin';

@Injectable({ providedIn: 'root' })
export class AccountAccessService {
    private unknownPlatformModeWarnings = new Set<string>();

    constructor(
        private readonly authStore: AuthStore,
        private readonly loggingService: LoggingService
    ) {}

    isApiTenantUser(): boolean {
        return this.resolveAccessMode() === 'api-tenant';
    }

    isTenantUser(): boolean {
        const accessMode = this.resolveAccessMode();
        return accessMode === 'tenant-base' || accessMode === 'admin';
    }

    isTenantBaseUser(): boolean {
        return this.resolveAccessMode() === 'tenant-base';
    }

    isAdminModeUser(): boolean {
        // Admin frontend access is currently contracted through AuthContext.platform_mode === Admin_Mode.
        // Backend must normalize every user that can access admin-only frontend pages to Admin_Mode.
        // Do not add role/claim checks here until the backend exposes a reliable role contract.
        return this.resolveAccessMode() === 'admin';
    }

    canAccessIntegration(): boolean {
        return this.isAdminModeUser();
    }

    canAccessApiKeys(): boolean {
        return this.isAdminModeUser();
    }

    getAccessDeniedRoute(): string {
        return APP_ROUTES.ACCESS_DENIED;
    }

    getDefaultRoute(): string {
        const accessMode = this.resolveAccessMode();

        if (accessMode === 'api-tenant') {
            return this.getIndividualDefaultRoute();
        }

        if (accessMode === 'tenant-base' || accessMode === 'admin') {
            return this.getTenantDefaultRoute();
        }

        return this.getAccessDeniedRoute();
    }

    getTenantDefaultRoute(): string {
        return APP_ROUTES.TENANT_DEFAULT;
    }

    getIndividualDefaultRoute(): string {
        return APP_ROUTES.WORKSPACE_DOCUMENTS;
    }

    getPreferredLayout(): AppLayoutType {
        const accessMode = this.resolveAccessMode();
        return accessMode === 'tenant-base' || accessMode === 'admin'
            ? APP_LAYOUTS.TENANT_DASHBOARD
            : APP_LAYOUTS.USER_SIMPLE;
    }

    getPlatformModeForLog(): string {
        return this.authStore.getPlatformMode() ?? 'unknown';
    }

    getPlatformMode(): PlatformMode | null {
        return this.authStore.getPlatformMode();
    }

    private resolveAccessMode(): ResolvedAccessMode | null {
        const platformMode = this.authStore.getPlatformMode();

        if (platformMode === PLATFORM_MODES.API_TENANT) {
            return 'api-tenant';
        }

        if (platformMode === PLATFORM_MODES.TENANT_BASE) {
            return 'tenant-base';
        }

        if (platformMode === PLATFORM_MODES.ADMIN_MODE) {
            return 'admin';
        }

        if (platformMode) {
            if (!this.unknownPlatformModeWarnings.has(platformMode)) {
                this.unknownPlatformModeWarnings.add(platformMode);
                this.loggingService.warn('[AccountAccessService] Unknown platform_mode detected. Denying route access.', {
                    platformMode,
                });
            }
            return null;
        }

        return null;
    }
}
